import { apiSyncProfile, getToken } from "./auth";

const PROFILE_KEY = "mateaventura.profile";

const DEFAULT_PROFILE = {
  name: "",
  avatarId: "monkey",
  points: 0,
  medals: [],
  streak: { count: 0, bestStreak: 0, lastDate: null },
  stats: {
    correct: 0,
    wrong: 0,
    rounds: 0,
    perOperation: {},
  },
};

// ---------- Date helpers ----------
export function todayKey(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function yesterdayKey(now = new Date()) {
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  return todayKey(y);
}

// ---------- Streak helpers ----------
function bumpStreak(streak) {
  const today = todayKey();
  const cur = streak || { count: 0, bestStreak: 0, lastDate: null };
  if (cur.lastDate === today) return cur; // already counted today
  const newCount = cur.lastDate === yesterdayKey() ? (cur.count || 0) + 1 : 1;
  return {
    count: newCount,
    bestStreak: Math.max(cur.bestStreak || 0, newCount),
    lastDate: today,
  };
}

// Returns { lost: bool, previousCount: number, profile: nextProfileOrSame }
export function checkAndResetLostStreak(profile) {
  const s = profile?.streak;
  if (!s || !s.lastDate || (s.count || 0) === 0) {
    return { lost: false, previousCount: 0, profile };
  }
  const today = todayKey();
  const yest = yesterdayKey();
  if (s.lastDate === today || s.lastDate === yest) {
    return { lost: false, previousCount: s.count, profile };
  }
  // Streak lost
  const next = {
    ...profile,
    streak: { count: 0, bestStreak: s.bestStreak || 0, lastDate: null },
  };
  saveProfile(next);
  return { lost: true, previousCount: s.count, profile: next };
}

// ---------- Profile CRUD ----------
export function loadProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PROFILE,
      ...parsed,
      streak: { ...DEFAULT_PROFILE.streak, ...(parsed.streak || {}) },
      stats: { ...DEFAULT_PROFILE.stats, ...(parsed.stats || {}) },
    };
  } catch {
    return null;
  }
}

export function saveProfile(profile) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  if (getToken()) {
    apiSyncProfile(profile).catch(() => {});
  }
}

export function createProfile(name, avatarId) {
  const p = { ...DEFAULT_PROFILE, name: name.trim(), avatarId };
  saveProfile(p);
  return p;
}

export function resetProfile() {
  localStorage.removeItem(PROFILE_KEY);
  if (getToken()) {
    apiSyncProfile(null).catch(() => {});
  }
}

export function recordRound(profile, { operationId, correct, wrong, pointsEarned, medalsUnlocked }) {
  const next = { ...profile };
  next.points = (next.points || 0) + (pointsEarned || 0);
  next.medals = Array.from(new Set([...(next.medals || []), ...(medalsUnlocked || [])]));
  next.stats = { ...next.stats };
  next.stats.correct = (next.stats.correct || 0) + correct;
  next.stats.wrong = (next.stats.wrong || 0) + wrong;
  next.stats.rounds = (next.stats.rounds || 0) + 1;
  next.stats.perOperation = { ...(next.stats.perOperation || {}) };
  const cur = next.stats.perOperation[operationId] || { correct: 0, wrong: 0, rounds: 0 };
  next.stats.perOperation[operationId] = {
    correct: cur.correct + correct,
    wrong: cur.wrong + wrong,
    rounds: cur.rounds + 1,
  };
  // Update daily streak — only triggered when a full round is completed
  next.streak = bumpStreak(next.streak);
  saveProfile(next);
  return next;
}
